import { inject, ChangeDetectionStrategy, Component, OnInit, input, model, output, signal } from '@angular/core';
import { LoggerService } from '@/core/services/logger.service';

import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Dialog } from 'primeng/dialog';
import { DatePicker } from 'primeng/datepicker';
import { Select } from 'primeng/select';
import { Button } from 'primeng/button';
import { TipoTelemedicinaResponse } from '@/core/api/sicom-api/registro/medico/telemedicina/telemedicina.model';
import { TelemedicinaFeatureService } from '../../telemedicina.service';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
export interface TelemedicinaDialogFormData {
    tipo: string;
    dataInicio?: Date | null;
    dataFinal?: Date | null;
}

@Component({
    selector: 'app-telemedicina-editar-dialog',
    standalone: true,
    imports: [DialogoPadraoDirective, ReactiveFormsModule, Dialog, DatePicker, Select, Button],
    templateUrl: './editar-telemedicina.html',
    styleUrl: './editar-telemedicina.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
/** Diálogo de edição de telemedicina. */
export class TelemedicinaEditarDialogComponent implements OnInit {
    private readonly logger = inject(LoggerService);
    private readonly fb = inject(FormBuilder);

    // Controle de visibilidade pelo componente pai (padrão do sistema via model())
    showDialog = model.required<boolean>();

    // Lista de tipos para o dropdown
    tipos = input<TipoTelemedicinaResponse[]>([]);

    // Fallback local quando o pai não informar os tipos (busca interna seguindo o padrão do sistema)
    tiposLocal = signal<TipoTelemedicinaResponse[]>([]);

    readonly telemedicinaForm = this.fb.group({
        tipo: this.fb.nonNullable.control('', Validators.required),
        dataInicio: this.fb.control<Date | null>(null, Validators.required),
        dataFinal: this.fb.control<Date | null>(null)
    });

    // Eventos para o pai tratar ações
    saved = output<TelemedicinaDialogFormData>();
    canceled = output<void>();

    constructor(private feature: TelemedicinaFeatureService) {}

    hide(): void {
        this.showDialog.set(false);
    }

    onCancel(): void {
        this.canceled.emit();
        this.hide();
    }

    ngOnInit(): void {
        // Se o componente pai não prover a lista de tipos, busca localmente via FeatureService
        if (!this.tipos() || this.tipos().length === 0) {
            this.feature.listarTiposTelemedicina().subscribe({
                next: (lista) => this.tiposLocal.set(lista || []),
                error: (err: Error) => this.logger.error('Erro ao carregar tipos de telemedicina (dialog)', err)
            });
        }
    }

    campoInvalido(nomeCampo: string): boolean {
        const campo = this.telemedicinaForm.get(nomeCampo);
        if (!campo) return false;
        return campo.invalid && (campo.touched || campo.dirty);
    }

    mensagemErroCampo(nomeCampo: string): string | null {
        const campo = this.telemedicinaForm.get(nomeCampo);
        if (!campo?.errors) return null;

        if (campo.errors['required']) return 'Campo obrigatório.';
        if (campo.errors['dataInvalida']) return 'Data inválida.';
        return 'Valor inválido.';
    }

    onSave(): void {
        if (this.telemedicinaForm.invalid) {
            this.telemedicinaForm.markAllAsTouched();
            return;
        }
        this.saved.emit({ ...this.telemedicinaForm.getRawValue() });
        this.hide();
    }

    // Fonte de opções do Select: usa o input do pai quando disponível; caso contrário, usa a lista carregada localmente
    tiposOptions(): TipoTelemedicinaResponse[] {
        const fornecidos = this.tipos();
        return fornecidos && fornecidos.length ? fornecidos : this.tiposLocal();
    }
}
